import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { Icons, Link, Table, TableRow, Icon, IconSizes, Flexbox, useTooltip } from '@skf-internal/ui-components-react-legacy';
import { GetEntityUrl, GetTraslationIdForEntityCategoryPlural } from 'utils';
import { RelationTurbineNullable } from 'domain/index';
import _ from 'lodash';
import { fromTurbineId } from '../../../../../domain/shared/ObjectId';
import { EntityState, stateToColor, stateToStyle } from '../../entity-state';
import { useRef } from 'react';

const WindTurbine = ({ windTurbines, editMode, onEditTurbine, onDeleteTurbine }: Props) => {
  const intl = useIntl();

  const tableHead: TableRow[] = [
    {
      cells: [
        { children: '', scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.name' }), scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.manufacturer' }), scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.marketShare' }), scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.remark' }), scope: 'col' },
        { children: '', scope: 'col' }
      ]
    }
  ];

  const tdStyle = 'align-top whitespace-normal';

  const tableBody: TableRow[] = windTurbines.map((windTurbine, index) => ({
    cells: [
      { width: '1%', className: stateToStyle(tdStyle, windTurbine.metadata?.state), children: <div title={'status'} className={`w-3 h-10 ${stateToColor(windTurbine.metadata?.state)}`}></div> },
      {
        width: '20%',
        className: stateToStyle(tdStyle, windTurbine.metadata?.state),
        children: <WindTurbineNameCell windTurbine={windTurbine} editMode={editMode} />
      },
      { width: '20%', className: stateToStyle(tdStyle, windTurbine.metadata?.state), children: <p>{windTurbine.turbine?.manufacturer?.manufacturerName ?? ''}</p> },
      { width: '10%', className: stateToStyle(tdStyle, windTurbine.metadata?.state), children: <p className="text-right">{windTurbine.marketShare}</p> },
      { width: '45%', className: stateToStyle(tdStyle, windTurbine.metadata?.state), children: <p>{windTurbine.remark}</p> },
      {
        width: '5%',
        className: 'align-top',
        children: (
          <>
            {editMode && windTurbine.metadata?.state !== EntityState.DELETED && windTurbine.metadata?.state !== EntityState.DELETED_UNSAVED && (
              <>
                <Link
                  as="button"
                  aria-label={intl.formatMessage({ id: 'windTurbine.button.edit' })}
                  feIcon={{ feIcon: Icons.Edit, position: 'left' }}
                  children=""
                  onClick={() => {
                    onEditTurbine(windTurbine, index);
                  }}
                />
                <Link
                  as="button"
                  aria-label={intl.formatMessage({ id: 'windTurbine.button.delete' })}
                  feIcon={{ feIcon: Icons.Trash, position: 'left' }}
                  children=""
                  onClick={() => {
                    onDeleteTurbine(
                      windTurbines.find((x) => x.turbineId === windTurbine.turbineId),
                      index
                    );
                  }}
                />
              </>
            )}
          </>
        )
      }
    ]
  }));

  return (
    <>
      {_.some(windTurbines) ? (
        <Table className="mt-5 mb-5" feBody={tableBody} feHead={tableHead} />
      ) : (
        <p className="mb-5">{`${intl.formatMessage({ id: 'windTurbine.empty.table' })} ${intl.formatMessage({ id: GetTraslationIdForEntityCategoryPlural('Turbine') })}`}</p>
      )}
    </>
  );
};

interface Props {
  windTurbines: RelationTurbineNullable[];
  editMode: boolean;
  onEditTurbine: any;
  onDeleteTurbine: any;
}

const WindTurbineNameCell = ({ windTurbine, editMode }: { windTurbine: RelationTurbineNullable; editMode: boolean }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { changesetId } = useParams();
  const { addPopover } = useTooltip();
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const handleMouseOver = () => {
    addPopover({ content: intl.formatMessage({ id: 'windTurbine.missing.tooltip' }), feType: 'tooltip' }, tooltipRef.current);
  };

  if (!windTurbine.turbine) {
    return (
      <Flexbox className="cursor-pointer" onMouseOver={handleMouseOver} ref={tooltipRef}>
        {intl.formatMessage({ id: 'windTurbine.missing' }, { id: windTurbine.turbineId })}
        <Icon className="ml-2" aria-label="Information" feIcon={Icons.InfoCircleOutlined} feSize={IconSizes.Lg} title="Test" />
      </Flexbox>
    );
  } else if (editMode) {
    return <p>{windTurbine.turbine?.turbineName ?? ''}</p>;
  } else {
    return <Link as="a" href={GetEntityUrl(fromTurbineId(windTurbine.turbineId), changesetId)} onClick={(_, route) => navigate(route)}>{`${windTurbine.turbine.turbineName}`}</Link>;
  }
};

export default WindTurbine;
